import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { eur, pct, OCC_LABELS, STATE_COLORS, scoreColor } from "@/lib/format";
import { Search, Loader2, SlidersHorizontal, Upload, FileSpreadsheet, FileDown, MapPin } from "lucide-react";
import UploadPeriziaDialog from "@/components/UploadPeriziaDialog";
import { PriorityBadge } from "@/components/PriorityBadge";
import { useAuth } from "@/context/AuthContext";
import { useDemo } from "@/context/DemoContext";
import { DemoBadge } from "@/components/DemoBadge";
import { toast } from "sonner";

function ScoreBadge({ score }) {
  return (
    <span className="inline-flex items-center justify-center w-11 font-mono text-sm font-medium px-2 py-0.5 rounded-sm" style={{ color: scoreColor(score), backgroundColor: `${scoreColor(score)}1a`, border: `1px solid ${scoreColor(score)}4d` }}>
      {score}
    </span>
  );
}

function EsitoBadge({ esito }) {
  const ok = esito === "QUALIFICATA";
  return (
    <span className={`text-[10px] font-medium uppercase tracking-wider px-2 py-0.5 rounded-sm border ${ok ? "text-[#10B981] bg-[#10B981]/10 border-[#10B981]/30" : "text-zinc-500 bg-zinc-500/10 border-zinc-500/30"}`}>
      {ok ? "Qualificata" : "Scartata"}
    </span>
  );
}

export default function DealDatabase() {
  const navigate = useNavigate();
  const { user } = useAuth();
  const { showDemo } = useDemo();
  const [deals, setDeals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [meta, setMeta] = useState({ comuni: [], strategie: [], province: [], fonti: [] });
  const [uploadOpen, setUploadOpen] = useState(false);
  const [filters, setFilters] = useState({ q: "", zona: "", raggio_km: "", comune: "", strategia: "", fonte: "", esito: "", priorita: "", roi_min: "", score_min: "", data_da: "", data_a: "", run_id: "", sort: "score" });
  const [fontiStats, setFontiStats] = useState([]);
  const [runs, setRuns] = useState([]);

  const buildParams = useCallback(() => {
    const params = Object.fromEntries(Object.entries(filters).filter(([, v]) => v !== "" && v != null));
    params.include_demo = showDemo;
    // raggio attivo: usa 'zona' come centro geografico (entro X km)
    if (params.raggio_km && params.zona) {
        params.centro = params.zona;
        delete params.zona;
    } else {
        delete params.raggio_km;
    }
    return params;
  }, [filters, showDemo]);

  const load = useCallback(() => {
    setLoading(true);
    api.get("/deals", { params: buildParams() }).then((r) => setDeals(r.data)).finally(() => setLoading(false));
  }, [buildParams]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => { api.get("/meta").then((r) => setMeta(r.data)).catch(() => {}); }, []);
  useEffect(() => {
    api.get("/deals/stats/fonti", { params: { include_demo: showDemo } }).then((r) => setFontiStats(r.data)).catch(() => {});
  }, [showDemo]);
  useEffect(() => { api.get("/ingestion/runs").then((r) => setRuns(r.data)).catch(() => {}); }, []);

  const set = (k, v) => setFilters((f) => ({ ...f, [k]: v }));
  const selectCls = "bg-[#122A3F] border border-white/10 rounded-sm px-3 py-2 text-sm text-white focus:outline-none focus:ring-1 focus:ring-[#EA7B2B]";

  const [exporting, setExporting] = useState("");
  const handleExport = async (fmt) => {
    setExporting(fmt);
    const params = buildParams();
    try {
      const r = await api.get(`/export/deals.${fmt}`, { params, responseType: "blob" });
      const cd = r.headers["content-disposition"] || "";
      const m = cd.match(/filename="?([^"]+)"?/);
      const fname = m ? m[1] : `rebreton_operazioni.${fmt}`;
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url; a.download = fname; document.body.appendChild(a); a.click();
      a.remove(); window.URL.revokeObjectURL(url);
      toast.success(`Export ${fmt.toUpperCase()} completato (${deals.length} operazioni)`);
    } catch (e) {
      toast.error("Errore durante l'export");
    } finally {
      setExporting("");
    }
  };

  return (
    <div className="space-y-6" data-testid="deals-page">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-2xl sm:text-3xl font-light text-white">Database operazioni</h1>
          <p className="text-sm text-zinc-500 mt-1">{deals.length} operazioni · aste, saldo e stralcio, NPL/UTP</p>
        </div>
        <div className="flex items-center flex-wrap gap-2">
          <button onClick={() => handleExport("xlsx")} disabled={exporting || !deals.length} data-testid="export-xlsx-btn" className="flex items-center gap-2 bg-[#122A3F] border border-white/15 text-white text-sm font-medium px-3.5 py-2.5 rounded-sm hover:border-[#EA7B2B]/60 transition-colors disabled:opacity-40">
            {exporting === "xlsx" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileSpreadsheet className="w-4 h-4 text-[#10B981]" />} Excel
          </button>
          <button onClick={() => handleExport("csv")} disabled={exporting || !deals.length} data-testid="export-csv-btn" className="flex items-center gap-2 bg-[#122A3F] border border-white/15 text-white text-sm font-medium px-3.5 py-2.5 rounded-sm hover:border-[#EA7B2B]/60 transition-colors disabled:opacity-40">
            {exporting === "csv" ? <Loader2 className="w-4 h-4 animate-spin" /> : <FileDown className="w-4 h-4 text-zinc-400" />} CSV
          </button>
          {(user?.role === "admin" || user?.role === "analista") && (
            <button onClick={() => setUploadOpen(true)} data-testid="upload-perizia-btn" className="flex items-center gap-2 bg-[#EA7B2B] text-black text-sm font-medium px-4 py-2.5 rounded-sm hover:bg-[#F59E4A] transition-colors">
              <Upload className="w-4 h-4" /> Analizza perizia PDF
            </button>
          )}
        </div>
      </div>

      {/* Filters */}
      <div className="bg-[#122A3F] border border-white/10 rounded-sm p-4 flex flex-wrap items-center gap-3" data-testid="deals-filters">
        <div className="flex items-center gap-2 bg-[#0A1826] border border-white/10 rounded-sm px-3 flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-zinc-500" />
          <input value={filters.q} onChange={(e) => set("q", e.target.value)} placeholder="Cerca RGE, comune, indirizzo, foglio, particella, custode, delegato, tribunale…" data-testid="deals-search" className="bg-transparent py-2 text-sm text-white focus:outline-none flex-1" />
        </div>
        <div className="flex items-center gap-2 bg-[#0A1826] border border-white/10 rounded-sm px-3 min-w-[190px]">
          <MapPin className="w-4 h-4 text-[#EA7B2B]" />
          <input value={filters.zona} onChange={(e) => set("zona", e.target.value)} placeholder="Zona (es. Roma, Milano, AL…)" data-testid="deals-zona" list="zona-suggestions" className="bg-transparent py-2 text-sm text-white focus:outline-none flex-1 w-full" />
          <datalist id="zona-suggestions">
            {meta.comuni.map((c) => <option key={"c-" + c} value={c} />)}
            {[...new Set(meta.province || [])].map((p) => <option key={"p-" + p} value={p} />)}
          </datalist>
        </div>
        <select value={filters.raggio_km} onChange={(e) => set("raggio_km", e.target.value)} data-testid="filter-raggio" title="Cerca entro un raggio dalla Zona indicata" className={selectCls}>
          <option value="">Raggio: —</option>
          <option value="10">entro 10 km</option>
          <option value="25">entro 25 km</option>
          <option value="50">entro 50 km</option>
          <option value="100">entro 100 km</option>
        </select>
        <select value={filters.priorita} onChange={(e) => set("priorita", e.target.value)} data-testid="filter-priorita" className={selectCls}>
          <option value="">Tutte le priorità</option>
          <option value="alta">🔴 Alta</option>
          <option value="media">🟠 Media</option>
          <option value="bassa">🟢 Bassa</option>
        </select>
        <select value={filters.comune} onChange={(e) => set("comune", e.target.value)} data-testid="filter-comune" className={selectCls}>
          <option value="">Tutti i comuni</option>
          {meta.comuni.map((c) => <option key={c} value={c}>{c}</option>)}
        </select>
        <select value={filters.strategia} onChange={(e) => set("strategia", e.target.value)} data-testid="filter-strategia" className={selectCls}>
          <option value="">Tutte le strategie</option>
          {meta.strategie.map((s) => <option key={s} value={s}>{s}</option>)}
        </select>
        <select value={filters.fonte} onChange={(e) => set("fonte", e.target.value)} data-testid="filter-fonte" className={selectCls}>
          <option value="">Tutte le fonti</option>
          {(meta.fonti || []).map((f) => <option key={f} value={f}>{f}</option>)}
        </select>
        {runs.length > 0 && (
          <select value={filters.run_id} onChange={(e) => set("run_id", e.target.value)} data-testid="filter-run" title="Filtra per singola esecuzione di ingestion" className={selectCls}>
            <option value="">Tutti i run di ingestion</option>
            {runs.filter((r) => (r.created || 0) > 0 || (r.enriched || 0) > 0).map((r) => {
              const d = r.started_at ? new Date(r.started_at).toLocaleString("it-IT", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" }) : "—";
              const zona = (r.zones && r.zones.length) ? ` · ${r.zones.join(",")}` : "";
              return <option key={r.id} value={r.id}>{d}{zona} · {r.created || 0} nuove</option>;
            })}
          </select>
        )}
        <div className="flex items-center gap-1 bg-[#122A3F] border border-white/10 rounded-sm px-2" title="Acquisite dal">
          <span className="text-xs text-zinc-500">Dal</span>
          <input type="date" value={filters.data_da} onChange={(e) => set("data_da", e.target.value)} data-testid="filter-data-da" className="bg-transparent py-2 text-sm text-white focus:outline-none" />
        </div>
        <div className="flex items-center gap-1 bg-[#122A3F] border border-white/10 rounded-sm px-2" title="Acquisite fino al">
          <span className="text-xs text-zinc-500">Al</span>
          <input type="date" value={filters.data_a} onChange={(e) => set("data_a", e.target.value)} data-testid="filter-data-a" className="bg-transparent py-2 text-sm text-white focus:outline-none" />
        </div>
        <select value={filters.esito} onChange={(e) => set("esito", e.target.value)} data-testid="filter-esito" className={selectCls}>
          <option value="">Qualificazione</option>
          <option value="QUALIFICATA">Qualificata</option>
          <option value="SCARTATA">Scartata</option>
        </select>
        <select value={filters.sort} onChange={(e) => set("sort", e.target.value)} data-testid="filter-sort" className={selectCls}>
          <option value="score">Ordina: Score</option>
          <option value="priorita">Ordina: Priorità</option>
          <option value="roi">Ordina: ROI</option>
          <option value="recent">Ordina: Recenti</option>
        </select>
        <div className="flex items-center gap-1 text-zinc-500 text-xs">
          <SlidersHorizontal className="w-3.5 h-3.5" /> {Object.values(filters).filter((v) => v && v !== "score").length} filtri
        </div>
      </div>

      {/* Contatore per fonte */}
      {fontiStats.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="fonti-counter">
          <span className="text-xs text-zinc-500 uppercase tracking-wider">Per fonte:</span>
          {fontiStats.map((f) => (
            <button
              key={f.fonte}
              onClick={() => set("fonte", filters.fonte === f.fonte ? "" : f.fonte)}
              data-testid={`fonte-chip-${f.fonte}`}
              className={`flex items-center gap-2 px-3 py-1 rounded-full text-xs border transition-colors ${filters.fonte === f.fonte ? "border-[#EA7B2B] text-[#EA7B2B] bg-[#EA7B2B]/10" : "border-white/10 text-zinc-300 hover:bg-white/5"}`}
            >
              {f.fonte}
              <span className="font-semibold">{f.totale}</span>
              <span className="text-[10px] text-emerald-400" title="Qualificate">✓{f.qualificate}</span>
            </button>
          ))}
        </div>
      )}

      {/* Table */}
      <div className="bg-[#122A3F] border border-white/10 rounded-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="sticky top-0 bg-[#0A1826]/90 backdrop-blur-md">
              <tr className="text-left text-xs text-zinc-500 uppercase tracking-wider border-b border-white/10">
                <th className="px-4 py-3 font-medium">Score</th>
                <th className="px-4 py-3 font-medium">Conf.</th>
                <th className="px-4 py-3 font-medium">Priorità</th>
                <th className="px-4 py-3 font-medium">Immobile</th>
                <th className="px-4 py-3 font-medium">Procedura</th>
                <th className="px-4 py-3 font-medium">Strategia</th>
                <th className="px-4 py-3 font-medium text-right">Prezzo base</th>
                <th className="px-4 py-3 font-medium text-right">Valore</th>
                <th className="px-4 py-3 font-medium text-right">ROI</th>
                <th className="px-4 py-3 font-medium">Occupazione</th>
                <th className="px-4 py-3 font-medium">Stato</th>
                <th className="px-4 py-3 font-medium">Esito</th>
              </tr>
            </thead>
            <tbody>
              {loading && (
                <tr><td colSpan={12} className="py-16 text-center"><Loader2 className="w-6 h-6 text-[#EA7B2B] animate-spin mx-auto" /></td></tr>
              )}
              {!loading && deals.length === 0 && (
                <tr><td colSpan={12} className="py-16 text-center text-zinc-500">Nessuna operazione trovata</td></tr>
              )}
              {!loading && deals.map((d) => (
                <tr key={d.id} onClick={() => navigate(`/operazioni/${d.id}`)} data-testid={`deal-row-${d.id}`} className="border-b border-white/5 hover:bg-white/5 cursor-pointer transition-colors">
                  <td className="px-4 py-3"><ScoreBadge score={d.score?.total} /></td>
                  <td className="px-4 py-3">
                    <span className="font-mono text-sm" style={{ color: (d.confidence?.total ?? 0) >= 70 ? "#10B981" : (d.confidence?.total ?? 0) >= 45 ? "#EA7B2B" : "#E11D48" }}>{d.confidence?.total ?? "—"}</span>
                    <span className="text-[10px] text-zinc-600"> · {d.completeness?.total ?? 0}%</span>
                  </td>
                  <td className="px-4 py-3"><PriorityBadge priority={d.priority} /></td>
                  <td className="px-4 py-3">
                    <div className="text-white flex items-center gap-2">{d.indirizzo}{d.is_demo && <DemoBadge />}</div>
                    <div className="text-xs text-zinc-500">{d.comune} ({d.provincia}) · {d.tipologia}</div>
                    <div className="flex gap-1.5 mt-1">
                      {d.multi_lotto && <span className="text-[9px] text-[#8B5CF6] border border-[#8B5CF6]/30 bg-[#8B5CF6]/10 px-1.5 py-0.5 rounded-sm uppercase tracking-wider">Lotto {d.lotto_label}/{d.totale_lotti}</span>}
                      {d.data_incompleta && <span className="text-[9px] text-[#D97706] border border-[#D97706]/30 bg-[#D97706]/10 px-1.5 py-0.5 rounded-sm uppercase tracking-wider">Da verificare</span>}
                    </div>
                  </td>
                  <td className="px-4 py-3 text-zinc-400 font-mono text-xs">{d.tribunale}<br />RGE {d.rge}</td>
                  <td className="px-4 py-3"><span className="text-xs text-[#EA7B2B]">{d.strategia}</span></td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-300">{eur(d.prezzo_base)}</td>
                  <td className="px-4 py-3 text-right font-mono text-zinc-400">{eur(d.valore_mercato)}</td>
                  <td className="px-4 py-3 text-right font-mono" style={{ color: d.financials?.roi > 20 ? "#10B981" : "#a1a1aa" }}>{pct(d.financials?.roi)}</td>
                  <td className="px-4 py-3 text-xs text-zinc-400">{OCC_LABELS[d.stato_occupazionale] || d.stato_occupazionale}</td>
                  <td className="px-4 py-3">
                    <span className="text-[10px] uppercase tracking-wider px-2 py-0.5 rounded-sm border" style={{ color: STATE_COLORS[d.stato], borderColor: `${STATE_COLORS[d.stato]}4d`, backgroundColor: `${STATE_COLORS[d.stato]}14` }}>{d.stato}</span>
                  </td>
                  <td className="px-4 py-3"><EsitoBadge esito={d.qualification?.esito} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <UploadPeriziaDialog open={uploadOpen} onClose={() => setUploadOpen(false)} onCreated={(id) => navigate(`/operazioni/${id}`)} />
    </div>
  );
}
